import { DataColumn, DataRow, DataRows, KpiWithChangeColumn, DataWarning } from '../../types';
import { ChartColors, DataSet } from '../../design/charts';
import {
  ChartData,
  ColorHelper,
  findDescriptionColumn,
  formatDataSets,
  getWarningsOfColumn,
  hasDataSetValues,
  makeDataSet,
} from './common';

export function createChartData(
  data: { rows: DataRows; totals?: DataRow; columns: DataColumn[]; fieldWarnings?: DataWarning[] },
  options: {
    yColumnName: string;
    xColumnName: string;
    currency: string;
    setPerRow?: boolean;
    includeTotals?: boolean;
    colorHelper?: ColorHelper;
    updatedYColumn?: DataColumn;
  }
): ChartData | undefined {
  const { rows, columns, totals, fieldWarnings } = data;
  const {
    yColumnName,
    xColumnName,
    updatedYColumn,
    currency,
    setPerRow = true,
    includeTotals = true,
    colorHelper,
  } = options;
  const xColumn = findDescriptionColumn(columns, xColumnName);

  const yColumn = columns.find(col => col.name === yColumnName);
  if (!xColumn || !yColumn) {
    return;
  }

  const myColorHelper = colorHelper
    ? colorHelper
    : new ColorHelper(
        xColumnName,
        rows.map(r => String(r.values[xColumn.name]))
      );
  const withChange = (yColumn as KpiWithChangeColumn)?.changeName !== undefined;

  const dataSets: DataSet[] = [];
  let totalsDataSet: DataSet | undefined;
  let dataLabels: string[] = [];

  if (setPerRow) {
    rows.forEach((row, index) => {
      dataSets.push(
        makeDataSet(row, {
          xColumn,
          yColumn,
          index,
          withChange,
          colorHelper: myColorHelper,
        })
      );
    });
  } else {
    dataLabels = [];
    const dataSet: DataSet = {
      values: {
        raw: [],
        formatted: [],
        fillColor: [],
        textColor: [],
      },
    };
    if (withChange) {
      dataSet.changes = {
        raw: [],
        formatted: [],
        textColor: [],
      };
    }

    const rowSets = rows.map((row, index) =>
      makeDataSet(row, {
        xColumn,
        yColumn,
        index,
        withChange,
        colorHelper: myColorHelper,
      })
    );
    rowSets.forEach(rowSet => {
      dataLabels.push(rowSet.label);
      const idx = rowSet.values.raw.length - 1;
      dataSet.values.raw.push(rowSet.values.raw[idx]);
      dataSet.values.fillColor.push(rowSet.values.fillColor[idx]);
      dataSet.values.textColor.push(rowSet.values.textColor[idx]);

      if (withChange) {
        dataSet.changes?.raw.push(rowSet.changes?.raw[idx]);
        dataSet.changes?.textColor.push(rowSet.changes?.textColor[idx]);
      }
    });

    dataSets.push(dataSet);
  }

  if (includeTotals && totals) {
    totalsDataSet = makeDataSet(totals, {
      xColumn,
      yColumn: updatedYColumn || yColumn,
      withChange,
      colorHelper: myColorHelper,
      index: 0,
      bgColor: ChartColors.panelBg,
    });
  }
  if (totalsDataSet) {
    formatDataSets([totalsDataSet], { yColumn: updatedYColumn || yColumn, currency });
  }

  formatDataSets(dataSets, { yColumn, currency });

  const hasData = hasDataSetValues(dataSets);

  const warnings = getWarningsOfColumn(yColumn.name, fieldWarnings);

  return {
    hasData,
    yColumn,
    updatedYColumn,
    dataSets,
    totalsDataSet,
    dataLabels,
    warnings,
  };
}
